// Partner cockpit — verwerking (processing) drill-in (B5B-PARTNER-COCKPIT §4/§5). One processing's detail
// inside the cockpit chrome: meta + its Doelen (purposes) + open-ticket count. Mandate re-checked
// server-side (403 if out of scope). Read-only. Behind real auth.
import { useParams, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, FileStack } from "lucide-react";
import { cockpit, CockpitError } from "../lib/cockpit";
import { humanize } from "../lib/format";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

function fmt(d: string | null): string { return d ? new Date(d).toLocaleDateString() : "—"; }

export function VerwerkingDetailRoute() {
  const { verwerkingId } = useParams({ from: "/verwerking/$verwerkingId" });
  const { data, isLoading, error } = useQuery({ queryKey: ["cockpit-verwerking", verwerkingId], queryFn: () => cockpit.verwerking(verwerkingId), retry: false });

  if (error instanceof CockpitError && (error.status === 403 || error.status === 404)) {
    return <div className="flex flex-col gap-4"><PageHeader title="Processing" /><Card><CardContent className="py-8 text-center text-sm text-destructive">This processing is outside your mandate.</CardContent></Card></div>;
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-3">
        <Button asChild size="sm" variant="ghost"><Link to="/search"><ArrowLeft className="size-4" /> Search</Link></Button>
      </div>
      {isLoading && <Skeleton className="h-24" />}
      {error && !(error instanceof CockpitError && (error.status === 403 || error.status === 404)) && (
        <Card><CardContent className="py-8 text-center text-sm text-destructive">{(error as Error).message}</CardContent></Card>
      )}
      {data && (
        <>
          <PageHeader title={data.name} description={data.tenantName} />
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="secondary">{humanize(data.status)}</Badge>
            <Badge variant="outline">{humanize(data.actingOrgRole)}</Badge>
            {data.heightenedRisk && <Badge variant="outline" className="text-amber-700">heightened risk</Badge>}
            {data.incidental && <Badge variant="outline">incidental</Badge>}
            {data.openTickets > 0 && <Badge variant="outline">{data.openTickets} open ticket(s)</Badge>}
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader><CardTitle className="text-base">Details</CardTitle></CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between gap-3"><span className="text-muted-foreground">Active from</span><span>{fmt(data.effectiveFrom)}</span></div>
                <div className="flex justify-between gap-3"><span className="text-muted-foreground">Active until</span><span>{data.effectiveTo ? fmt(data.effectiveTo) : "open-ended"}</span></div>
                {data.description && <p className="whitespace-pre-line border-t pt-2 text-muted-foreground">{data.description}</p>}
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2 text-base"><FileStack className="size-4" />Purposes (Doelen)</CardTitle></CardHeader>
              <CardContent>
                {data.doelen.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No purposes recorded.</p>
                ) : (
                  <ul className="m-0 list-none space-y-2 p-0 text-sm">
                    {data.doelen.map((d) => (
                      <li key={d.id} className="flex items-center justify-between gap-3 border-b py-1.5 last:border-b-0">
                        <span className="min-w-0 truncate">{d.name}</span>
                        <Badge variant="outline">{humanize(d.status)}</Badge>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
